import { useMutation, useQueryClient } from "@tanstack/react-query";
import { authClient } from "@/lib/auth";
import { useTRPC } from "@/lib/trpc";

export const useUpdateUserRole = () => {
    const trpc = useTRPC();
    const queryClient = useQueryClient();

    const opts = trpc.user.updateRole.mutationOptions({
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: trpc.user.members.pathKey(),
            });
        },
    });

    return useMutation(opts);
};

export const useRemoveUser = () => {
    const trpc = useTRPC();
    const queryClient = useQueryClient();

    const opts = trpc.user.removeMember.mutationOptions({
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: trpc.user.members.pathKey(),
            });
        },
    });

    return useMutation(opts);
};

export const useUpdateUserName = () => {
    const trpc = useTRPC();
    const session = authClient.useSession();

    const opts = trpc.user.updateName.mutationOptions({
        onSuccess: (_, _vars, _on, context) => {
            // Invalidate auth session to refresh user data
            session.refetch();
            context.client.invalidateQueries({ queryKey: ["session"] });
        },
    });

    return useMutation(opts);
};

export const useInviteUser = () => {
    const trpc = useTRPC();
    const queryClient = useQueryClient();

    const opts = trpc.user.inviteUser.mutationOptions({
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: trpc.user.members.pathKey(),
            });
            queryClient.invalidateQueries({
                queryKey: trpc.user.invitations.pathKey(),
            });
        },
    });

    return useMutation(opts);
};

export const useRemoveInvitation = () => {
    const trpc = useTRPC();
    const queryClient = useQueryClient();

    const opts = trpc.user.removeInvitation.mutationOptions({
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: trpc.user.invitations.pathKey(),
            });
        },
    });

    return useMutation(opts);
};

/**
 * Optimistic: the preferences cache is what every formatted date reads, so the whole app
 * switches notation the moment a value is picked. Rolled back if the server rejects it.
 */
export const useUpdateUserPreferences = () => {
    const trpc = useTRPC();
    const queryClient = useQueryClient();
    const queryKey = trpc.user.preferences.queryKey();

    const opts = trpc.user.updatePreferences.mutationOptions({
        onMutate: async (patch) => {
            await queryClient.cancelQueries({ queryKey });
            const previous = queryClient.getQueryData(queryKey);
            if (previous) queryClient.setQueryData(queryKey, { ...previous, ...patch });
            return { previous };
        },
        onError: (_error, _patch, context) => {
            if (context?.previous) queryClient.setQueryData(queryKey, context.previous);
        },
        onSuccess: (preferences) => {
            queryClient.setQueryData(queryKey, preferences);
        },
    });

    return useMutation(opts);
};
